import { useState, useContext, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../../../context/user.context";
import { getLoansByDealership } from "../../../utils/core";
import {
  getFirstAllowedRoute,
  isRouteAllowed,
} from "../../../utils/routing/routePermissions";

const hasLoanPermissions = (permissions = []) =>
  permissions.includes("loans.view_unit") ||
  permissions.includes("loans.view_own");

export const useNavBar = () => {
  const {
    currentUser,
    requestSignOut,
    beginUnitSelection,
    availableWorkspaces,
  } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const [anchorEl, setAnchorEl] = useState({});
  const [isSoporteModalOpen, setIsSoporteModalOpen] = useState(false);

  // Préstamos firmados cuyo horario estipulado ya pasó y nadie los inició:
  // badge en la entrada "Préstamos" para que el operador los inicie o cancele.
  const [pendingLoanStarts, setPendingLoanStarts] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const dealershipId = currentUser?.dealershipId || currentUser?.id;
    const operatingUnitId = currentUser?.activeUnitId || null;
    if (!hasLoanPermissions(currentUser?.permissions) || !dealershipId) {
      setPendingLoanStarts(0);
      return undefined;
    }
    (async () => {
      try {
        const res = await getLoansByDealership({
          dealershipId,
          operatingUnitId,
          status: "signed",
        });
        if (cancelled) return;
        const now = Date.now();
        const count = (res.data || []).filter(
          (loan) =>
            loan.startAt && new Date(loan.startAt).getTime() <= now,
        ).length;
        setPendingLoanStarts(count);
      } catch {
        if (!cancelled) setPendingLoanStarts(0);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    currentUser?.dealershipId,
    currentUser?.id,
    currentUser?.activeUnitId,
    currentUser?.permissions,
    location.pathname,
  ]);

  const handleMenuEnter = (menuId, event) => {
    setAnchorEl({ [menuId]: event.currentTarget });
  };

  const handleMenuToggle = (menuId, event) => {
    if (anchorEl[menuId]) {
      handleMenuClose(menuId);
    } else {
      handleMenuEnter(menuId, event);
    }
  };

  const handleMenuClose = (menuId) => {
    // Remover el foco antes de cerrar: un MenuItem enfocado dentro de un
    // Menu que se oculta (aria-hidden) dispara el warning de React y rompe
    // accesibilidad. Cubre también la navegación (disableRestoreFocus).
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setAnchorEl((prev) => {
      const newAnchorEl = { ...prev };
      delete newAnchorEl[menuId];
      return newAnchorEl;
    });
  };

  const handleNavigate = (path, menuId) => {
    if (menuId) handleMenuClose(menuId);
    // GUARD-005: nunca navegar a una ruta sin el permiso (deep link / URL manual).
    if (!isRouteAllowed(path, currentUser)) {
      navigate(getFirstAllowedRoute(currentUser) || "/access-denied", {
        replace: true,
      });
      return;
    }
    navigate(path);
  };

  const handleLogout = () => {
    // Con varios tipos de usuario se pregunta primero (cambiar de usuario o
    // cerrar sesión); con uno solo cierra sesión directamente.
    requestSignOut();
  };

  const handleOpenSoporteModal = (menuId) => {
    if (menuId) handleMenuClose(menuId);
    setIsSoporteModalOpen(true);
  };

  const handleCloseSoporteModal = () => setIsSoporteModalOpen(false);

  return {
    currentUser,
    anchorEl,
    isSoporteModalOpen,
    pendingLoanStarts,
    handleMenuToggle,
    handleMenuClose,
    handleNavigate,
    handleLogout,
    handleOpenSoporteModal,
    handleCloseSoporteModal,
    beginUnitSelection,
    canChangeWorkspace: availableWorkspaces.length > 1,
  };
};

/*
  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/sign-in');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
      navigate('/sign-in');
    }
  };
*/
